import type { HttpClient } from '../http.js';
import { TagadaNotFoundError } from '../errors.js';
import {
  applePayContactToAddress,
  googlePayContactToAddress,
  type ApplePayContact,
  type GooglePayContact,
} from '../helpers/walletContact.js';
import type { CheckoutModule } from './checkout.js';
import type {
  Address,
  Environment,
  PaymentSetupConfig,
  PaymentMethodConfig,
  ExpressMethods,
  StoreConfig,
  PayResult,
  PayOptions,
  PayDirectOptions,
  CreateInstrumentOptions,
  PaymentInstrument,
  ThreedsSession,
  PaymentStatus,
  ProcessPaymentOptions,
  ProcessPaymentResult,
} from '../types.js';

/**
 * One row of `GET /api/v1/payment-methods` — the checkout's payment-method
 * picker list. `type` is `'credit-card'` for the native card form, otherwise
 * the integration provider (`apple_pay`, `google_pay`, `paypal`, `klarna`, …).
 */
interface PaymentMethodListEntry {
  id: string;
  type: string;
  title?: string;
  iconUrl?: string;
  default?: boolean;
  settings?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

const EXPRESS_METHOD_TYPES = new Set(['apple_pay', 'google_pay', 'paypal', 'klarna', 'klarna_express', 'link']);

/** Map a picker-list row to the `PaymentSetupConfig` entry shape (#TAG-2246). */
function paymentMethodListEntryToConfig(entry: PaymentMethodListEntry): PaymentMethodConfig {
  const isCard = entry.type === 'credit-card';
  const method = isCard ? 'card' : entry.type;
  const settings = (entry.settings ?? {}) as Record<string, unknown>;
  const processors = settings.processors as Array<{ processorId?: string }> | undefined;
  const publishableKey = settings.publishableKey;
  return {
    enabled: true,
    method,
    provider: isCard ? undefined : entry.type,
    type: isCard ? 'card' : entry.type,
    label: entry.title,
    logoUrl: entry.iconUrl,
    express: EXPRESS_METHOD_TYPES.has(method),
    integrationId: isCard ? undefined : entry.id,
    processorId: processors?.[0]?.processorId,
    publishableKey: typeof publishableKey === 'string' ? publishableKey : undefined,
    metadata: entry.metadata ?? undefined,
  };
}

/**
 * Full payment lifecycle -- discover APMs, tokenize, create instruments,
 * process payments, handle 3DS, and poll for status.
 *
 * Wraps the same REST endpoints that plugin-sdk uses internally.
 *
 * @remarks PCI DSS compliance notice
 *
 * This SDK collects card data (PAN, expiry, CVC) in the merchant's page
 * context and sends it directly to Basis Theory for tokenization. Card data
 * never reaches TagadaPay servers.
 *
 * Because card data transits through the merchant's JavaScript environment,
 * merchants using this SDK are responsible for **PCI DSS SAQ-A-EP**
 * compliance. This includes quarterly ASV scans, secure page delivery
 * (TLS), and ensuring no unauthorized scripts can access cardholder data.
 *
 * TagadaPay is PCI DSS Level 1 certified. The merchant's PCI scope is
 * determined by their integration method, not by TagadaPay's certification.
 *
 * See: https://docs.tagada.io/developer-tools/headless-sdk/pci-compliance
 */
export class PaymentModule {
  /**
   * Last payment setup seen by `getPaymentSetup` — lets `processApplePay`
   * resolve the domain's Apple Pay registration (BYOK / platform-integrator)
   * without a second network round-trip inside Apple's authorization window.
   */
  private lastPaymentSetup: PaymentSetupConfig | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
    private readonly environment: Environment,
    /** Used to persist wallet contacts onto the session before charging. */
    private readonly checkout?: CheckoutModule,
  ) {}

  // ---------------------------------------------------------------------------
  // Discovery: what payment methods are available?
  // ---------------------------------------------------------------------------

  /**
   * Get the full payment setup config for a checkout session.
   * Returns a map of payment methods with their configuration
   * (card, apple_pay, google_pay, klarna, etc.).
   *
   * Priority chain:
   * 1. Explicit `paymentSetupConfig` parameter (developer override)
   * 2. `window.__TGD_STEP_CONFIG__` injection (Tagada routing infra)
   * 3. API fallback (`GET /api/v1/payment-methods`)
   */
  async getPaymentSetup(
    checkoutSessionId: string,
    opts?: { paymentSetupConfig?: PaymentSetupConfig },
  ): Promise<PaymentSetupConfig> {
    if (opts?.paymentSetupConfig) {
      this.lastPaymentSetup = opts.paymentSetupConfig;
      return opts.paymentSetupConfig;
    }

    const injected = this.readInjectedStepConfig();
    if (injected) {
      this.lastPaymentSetup = injected;
      return injected;
    }

    // The canonical config for the session — same builder as the step config
    // injected on Tagada-hosted pages and the partner API-key route
    // (#TAG-2246). Before this route existed the fallback read a `methods`
    // envelope off `/api/v1/payment-methods` (a flat array) and returned `{}`
    // for every self-hosted page.
    try {
      const res = await this.http.get<{ config?: PaymentSetupConfig } | null | undefined>(
        `/api/v1/checkout-sessions/${checkoutSessionId}/payment-setup`,
      );
      const config: PaymentSetupConfig = { ...(res?.config ?? {}) };
      this.lastPaymentSetup = config;
      return config;
    } catch (err) {
      // Backend older than the route: derive the config from the hosted
      // picker list so an upgraded SDK keeps working until the API catches up.
      if (!(err instanceof TagadaNotFoundError)) throw err;
    }

    const config = await this.getPaymentSetupFromPickerList(checkoutSessionId);
    this.lastPaymentSetup = config;
    return config;
  }

  /**
   * Transitional fallback: `/api/v1/payment-methods` answers with a flat
   * array of `{ id, type, title, iconUrl, default, settings, metadata }`
   * picker rows (`type` = 'credit-card' or the integration provider). Map
   * them to the setup-config shape. Only used when the backend does not
   * serve `/checkout-sessions/:id/payment-setup` yet.
   */
  private async getPaymentSetupFromPickerList(checkoutSessionId: string): Promise<PaymentSetupConfig> {
    const res = await this.http.get<
      PaymentMethodListEntry[] | { methods?: PaymentMethodConfig[] } | null | undefined
    >('/api/v1/payment-methods', { params: { checkoutSessionId } });

    const entries: PaymentMethodConfig[] = Array.isArray(res)
      ? res.map(paymentMethodListEntryToConfig)
      : (res?.methods ?? []);

    const config: PaymentSetupConfig = {};
    for (const m of entries) {
      const key = m.provider && m.provider !== m.method ? `${m.method}:${m.provider}` : m.method;
      config[key] = m;
    }
    return config;
  }

  /**
   * Get express payment methods (Apple Pay, Google Pay, PayPal, etc.)
   * filtered to only those available in the current browser.
   */
  async getExpressMethods(
    checkoutSessionId: string,
    opts?: { paymentSetupConfig?: PaymentSetupConfig },
  ): Promise<ExpressMethods> {
    const setup = await this.getPaymentSetup(checkoutSessionId, opts);
    const result: ExpressMethods = {};

    for (const [key, config] of Object.entries(setup)) {
      if (!config.enabled) continue;

      // Handle express_checkout:processorId entries (from paymentSetupConfig injection)
      // These have a `methods` map like { apple_pay: { enabled: true }, google_pay: { enabled: true } }
      if (key.startsWith('express_checkout:') && config.methods) {
        const base = {
          available: true,
          processorId: config.processorId,
          publishableKey: config.publishableKey,
          metadata: config.metadata,
        };
        if (config.methods.apple_pay?.enabled) {
          const hasApplePay =
            typeof window !== 'undefined' &&
            'ApplePaySession' in window &&
            (window as unknown as Record<string, { canMakePayments?: () => boolean }>)
              .ApplePaySession?.canMakePayments?.();
          result.applePay = { ...base, type: 'apple_pay', available: !!hasApplePay };
        }
        if (config.methods.google_pay?.enabled) {
          result.googlePay = { ...base, type: 'google_pay' };
        }
        if (config.methods.link?.enabled) {
          result.link = { ...base, type: 'link' };
        }
        if (config.methods.klarna_express?.enabled) {
          result.klarna = { ...base, type: 'klarna_express' };
        }
        continue;
      }

      if (!config.express) continue;

      const method = config.method;
      const base = {
        type: method,
        available: true,
        processorId: config.processorId,
        publishableKey: config.publishableKey,
        metadata: config.metadata,
      };

      if (method === 'apple_pay' || config.type === 'apple_pay') {
        const hasApplePay =
          typeof window !== 'undefined' &&
          'ApplePaySession' in window &&
          (window as unknown as Record<string, { canMakePayments?: () => boolean }>)
            .ApplePaySession?.canMakePayments?.();
        result.applePay = { ...base, available: !!hasApplePay };
      } else if (method === 'google_pay' || config.type === 'google_pay') {
        result.googlePay = base;
      } else if (method === 'paypal') {
        result.paypal = base;
      } else if (method === 'klarna' || method === 'klarna_express') {
        result.klarna = base;
      } else if (method === 'link') {
        result.link = base;
      }
    }

    return result;
  }

  /**
   * Get store-level payment config (3DS flags, default flow ID, etc.).
   */
  async getStoreConfig(): Promise<StoreConfig> {
    const res = await this.http.get<{ config: StoreConfig }>(
      '/api/v1/checkout/config',
      { params: { storeId: this.storeId } },
    );
    return res.config ?? (res as unknown as StoreConfig);
  }

  /**
   * Convenience: return only enabled payment method keys.
   */
  async getEnabledMethods(
    checkoutSessionId: string,
    opts?: { paymentSetupConfig?: PaymentSetupConfig },
  ): Promise<string[]> {
    const setup = await this.getPaymentSetup(checkoutSessionId, opts);
    return Object.entries(setup)
      .filter(([, c]) => c.enabled)
      .map(([key]) => key);
  }

  // ---------------------------------------------------------------------------
  // Tokenization (delegates to @tagadapay/core-js if available)
  // ---------------------------------------------------------------------------

  /**
   * Tokenize a card using @tagadapay/core-js.
   * Requires `@tagadapay/core-js` to be installed as a peer dependency.
   *
   * Card data is sent directly to Basis Theory for tokenization and never
   * reaches TagadaPay servers. The returned `tagadaToken` is safe to pass
   * to the TagadaPay API.
   *
   * **PCI note:** Using this method means card data passes through your
   * JavaScript environment. You are responsible for PCI DSS SAQ-A-EP
   * compliance. See https://docs.tagada.io/developer-tools/headless-sdk/pci-compliance
   *
   * @returns tagadaToken (base64 string) and rawToken with SCA metadata.
   */
  async tokenizeCard(card: {
    cardNumber: string;
    expiryDate: string;
    cvc: string;
    cardholderName?: string;
  }): Promise<{ tagadaToken: string; rawToken: Record<string, unknown> }> {
    const coreJs = await this.loadCoreJs();

    const tokenizer = new coreJs.Tokenizer({ environment: this.environment });
    await tokenizer.initialize();

    const sanitized: Record<string, string> = {
      cardNumber: card.cardNumber,
      expiryDate: card.expiryDate,
      cvc: card.cvc,
    };
    if (card.cardholderName) sanitized.cardholderName = card.cardholderName;

    // Tokenize ONCE — both `tokenizer.tokenizeCard` and
    // `tokenizer.tokenizeCardRaw` internally call BasisTheory's
    // `POST /tokens`. Running them in `Promise.all` charged the
    // customer's network with two identical tokenization requests
    // (visible in the browser as duplicated `tokens/` calls). Get
    // the raw response once, then derive the encoded TagadaToken
    // locally via `createTagadaToken`.
    const rawToken = await tokenizer.tokenizeCardRaw(sanitized);
    const tagadaToken: string = coreJs.createTagadaToken(
      rawToken,
      tokenizer.getProvider().getProviderName(),
    );

    return { tagadaToken, rawToken };
  }

  // ---------------------------------------------------------------------------
  // Instrument creation
  // ---------------------------------------------------------------------------

  /**
   * Create a reusable payment instrument from a TagadaToken.
   * Session-authenticated (uses the checkout session JWT).
   */
  async createInstrument(
    opts: CreateInstrumentOptions,
  ): Promise<{ paymentInstrument: PaymentInstrument }> {
    return this.http.post('/api/v1/payment/create-instrument-from-token', {
      tagadaToken: opts.tagadaToken,
    });
  }

  // ---------------------------------------------------------------------------
  // 3DS
  // ---------------------------------------------------------------------------

  /**
   * Create a 3DS session on the TagadaPay backend.
   * This persists the session so the processor can use it during payment.
   */
  async create3dsSession(opts: {
    paymentInstrumentId: string;
    sessionData: { sessionId: string; metadata?: Record<string, unknown> };
    provider?: string;
  }): Promise<ThreedsSession> {
    return this.http.post('/api/public/v1/threeds/create-session', {
      provider: opts.provider ?? 'basis_theory',
      storeId: this.storeId,
      paymentInstrumentId: opts.paymentInstrumentId,
      sessionData: opts.sessionData,
    });
  }

  // ---------------------------------------------------------------------------
  // Payment processing
  // ---------------------------------------------------------------------------

  /**
   * High-level pay: single API call that decodes tagadaToken server-side,
   * creates the payment instrument, and processes payment.
   *
   * Also accepts `paymentInstrumentId` directly for pre-created instruments.
   */
  async pay(opts: PayOptions): Promise<PayResult> {
    if (opts.tagadaToken && !opts.paymentInstrumentId) {
      return this.http.post<PayResult>('/api/v1/payment/pay-with-token', {
        tagadaToken: opts.tagadaToken,
        checkoutSessionId: opts.checkoutSessionId,
        returnUrl: opts.returnUrl,
        paymentFlowId: opts.paymentFlowId,
        processorId: opts.processorId,
        paymentMethod: opts.paymentMethod,
        isExpress: opts.isExpress,
        metadata: opts.metadata,
      });
    }

    return this.http.post<PayResult>('/api/public/v1/checkout/pay-v2', {
      checkoutSessionId: opts.checkoutSessionId,
      paymentInstrumentId: opts.paymentInstrumentId,
      storeId: this.storeId,
      paymentFlowId: opts.paymentFlowId,
      threedsSessionId: opts.threedsSessionId,
      processorId: opts.processorId,
      paymentMethod: opts.paymentMethod,
      isExpress: opts.isExpress,
      metadata: opts.metadata,
      returnUrl: opts.returnUrl,
    });
  }

  /**
   * Low-level pay: process payment with an existing instrument or for APM/express flows
   * where no instrument is needed (redirect-based methods).
   */
  async payDirect(opts: PayDirectOptions): Promise<PayResult> {
    return this.http.post<PayResult>('/api/public/v1/checkout/pay-v2', {
      checkoutSessionId: opts.checkoutSessionId,
      paymentInstrumentId: opts.paymentInstrumentId ?? '',
      storeId: this.storeId,
      paymentFlowId: opts.paymentFlowId,
      processorId: opts.processorId,
      paymentMethod: opts.paymentMethod,
      isExpress: opts.isExpress,
      threedsSessionId: opts.threedsSessionId,
      returnUrl: opts.returnUrl,
    });
  }

  /**
   * Persist a wallet contact onto the session before the charge.
   *
   * Express wallets are the one checkout where the buyer never fills an address
   * form: they approve one inside the sheet, and it comes back on the
   * authorization event. Studio's `ExpressPaymentWidget` writes it to the
   * session; the headless path used to drop it, which is how a year of orders
   * reached merchants with NULL shipping, billing and customer addresses
   * (#TAG-1989).
   *
   * Never invents data: a contact with no street line or no country maps to
   * `undefined` and the session is left exactly as it was. Failures are NOT
   * swallowed — if we hold an address and cannot store it, the charge must not
   * proceed, or we are back to the bug this fixes.
   */
  private async syncWalletContact(
    checkoutSessionId: string,
    contact: {
      shippingAddress?: Address;
      billingAddress?: Address;
      email?: string;
      phone?: string;
      firstName?: string;
      lastName?: string;
    },
  ): Promise<void> {
    const { shippingAddress, billingAddress } = contact;
    if (!shippingAddress && !billingAddress) return;

    if (!this.checkout) {
      throw new Error(
        'PaymentModule cannot persist the wallet contact: no CheckoutModule was provided. ' +
          'Build the SDK through createTagadaClient() rather than constructing PaymentModule directly.',
      );
    }

    const customer: Record<string, string> = {};
    if (contact.email) customer.email = contact.email;
    if (contact.phone) customer.phone = contact.phone;
    if (contact.firstName) customer.firstName = contact.firstName;
    if (contact.lastName) customer.lastName = contact.lastName;

    await this.checkout.updateCustomerAndAddress(checkoutSessionId, {
      customer,
      shippingAddress,
      billingAddress,
    });
  }

  /**
   * Process an Apple Pay payment.
   * Tokenizes the Apple Pay token and processes payment in one call.
   *
   * Pass the contacts from `onpaymentauthorized` — `event.payment.billingContact`
   * and `event.payment.shippingContact` — and they are written to the session
   * before the charge, so the address the buyer approved in the wallet sheet
   * lands on the order (#TAG-1989).
   *
   * Apple only returns them when the payment request asked for them, so the
   * `ApplePaySession` request needs:
   *
   * ```js
   * requiredBillingContactFields: ['postalAddress'],
   * requiredShippingContactFields: ['name', 'phone', 'email', 'postalAddress'],
   * ```
   *
   * @example
   * session.onpaymentauthorized = async (event) => {
   *   await tagada.payment.processApplePay({
   *     checkoutSessionId,
   *     applePayToken: event.payment.token,
   *     billingContact: event.payment.billingContact,
   *     shippingContact: event.payment.shippingContact,
   *   });
   * };
   */
  async processApplePay(opts: {
    checkoutSessionId: string;
    applePayToken: unknown;
    billingContact?: ApplePayContact | null;
    shippingContact?: ApplePayContact | null;
    paymentFlowId?: string;
    processorId?: string;
    /**
     * Basis Theory Apple Pay registration the checkout domain is bound to.
     * Required when the domain runs merchant-scoped Apple Pay — BYOK or the
     * platform-integrator (PPIID) system — because Apple encrypts the token
     * to that registration's certificate. Resolved automatically from the
     * payment setup (`applePayByok` domain map) when omitted; pass it only to
     * override that resolution.
     */
    merchantRegistrationId?: string;
  }): Promise<PayResult> {
    await this.syncWalletContact(opts.checkoutSessionId, {
      shippingAddress: applePayContactToAddress(opts.shippingContact),
      billingAddress: applePayContactToAddress(opts.billingContact),
      email: opts.shippingContact?.emailAddress ?? opts.billingContact?.emailAddress,
      phone: opts.shippingContact?.phoneNumber ?? opts.billingContact?.phoneNumber,
      firstName: opts.shippingContact?.givenName ?? opts.billingContact?.givenName,
      lastName: opts.shippingContact?.familyName ?? opts.billingContact?.familyName,
    });

    const merchantRegistrationId =
      opts.merchantRegistrationId ??
      (await this.resolveApplePayRegistration(opts.checkoutSessionId))?.merchantRegistrationId;

    const coreJs = await this.loadCoreJs();
    const tokenizer = new coreJs.Tokenizer({ environment: this.environment });
    await tokenizer.initialize();
    // The tokenizer returns the normalized Basis Theory response — it carries
    // no `tagadaToken` field. Wrap it into the standard base64 TagadaToken the
    // pay endpoint expects (destructuring `{ tagadaToken }` here used to yield
    // undefined and silently push the charge down the instrument-less path).
    const rawToken = await tokenizer.tokenizeApplePay(opts.applePayToken, merchantRegistrationId);
    const tagadaToken = btoa(
      JSON.stringify(coreJs.createTagadaDigitalWalletToken(rawToken, 'apple_pay')),
    );

    return this.pay({
      checkoutSessionId: opts.checkoutSessionId,
      tagadaToken,
      paymentFlowId: opts.paymentFlowId,
      processorId: opts.processorId,
      paymentMethod: 'apple_pay',
      isExpress: true,
    });
  }

  /**
   * Resolve the Apple Pay registration binding for the domain the page is
   * served from. Both merchant-certificate (BYOK) and platform-integrator
   * (PPIID) Apple Pay attach a domain-keyed `applePayByok` map to the
   * `apple_pay` method's metadata; a hit means every Basis Theory call —
   * merchant session AND tokenization — must carry that
   * `merchant_registration_id`. A miss (the default) means legacy platform
   * Apple Pay: omit the id and behavior is unchanged.
   *
   * Sources, in order: the Tagada routing injection, the last payment setup
   * seen by `getPaymentSetup`, then one API fetch when a `checkoutSessionId`
   * is given and no setup was ever loaded.
   *
   * Use `displayName` (when present) as the `display_name` of the Basis
   * Theory merchant session.
   */
  async resolveApplePayRegistration(
    checkoutSessionId?: string,
  ): Promise<{ merchantRegistrationId: string; displayName?: string } | null> {
    if (typeof window === 'undefined') return null;

    let host = window.location.hostname.toLowerCase().split(':')[0];
    if (host.startsWith('www.')) host = host.slice(4);

    const fromConfig = (setup: PaymentSetupConfig | null) => {
      for (const entry of Object.values(setup ?? {})) {
        const map = entry?.metadata?.applePayByok as
          | Record<string, { merchantRegistrationId?: string; displayName?: string }>
          | undefined;
        if (!map || typeof map !== 'object') continue;
        const hit = map[host];
        if (hit?.merchantRegistrationId) {
          return { merchantRegistrationId: hit.merchantRegistrationId, displayName: hit.displayName };
        }
      }
      return null;
    };

    const local = fromConfig(this.readInjectedStepConfig()) ?? fromConfig(this.lastPaymentSetup);
    if (local) return local;
    // A setup we already hold that carries no binding is authoritative — don't
    // burn a network round-trip (inside Apple's authorization window) to
    // re-learn that this is a legacy platform domain.
    if (this.lastPaymentSetup || this.readInjectedStepConfig() || !checkoutSessionId) return null;
    try {
      return fromConfig(await this.getPaymentSetup(checkoutSessionId));
    } catch {
      return null;
    }
  }

  /**
   * Process a Google Pay payment.
   * Tokenizes the Google Pay token and processes payment in one call.
   *
   * Pass `paymentData.shippingAddress` / the billing address off
   * `paymentMethodData.info.billingAddress` and they are persisted to the
   * session before the charge (#TAG-1989). Google only returns them when the
   * request sets `shippingAddressRequired` / `billingAddressRequired`.
   */
  async processGooglePay(opts: {
    checkoutSessionId: string;
    googlePayToken: string;
    billingAddress?: GooglePayContact | null;
    shippingAddress?: GooglePayContact | null;
    email?: string;
    paymentFlowId?: string;
    processorId?: string;
  }): Promise<PayResult> {
    await this.syncWalletContact(opts.checkoutSessionId, {
      shippingAddress: googlePayContactToAddress(opts.shippingAddress),
      billingAddress: googlePayContactToAddress(opts.billingAddress),
      email: opts.email ?? opts.shippingAddress?.emailAddress ?? opts.billingAddress?.emailAddress,
      phone: opts.shippingAddress?.phoneNumber ?? opts.billingAddress?.phoneNumber,
    });

    const coreJs = await this.loadCoreJs();
    const tokenizer = new coreJs.Tokenizer({ environment: this.environment });
    await tokenizer.initialize();
    // Same wrapping as Apple Pay above — the tokenizer's normalized response
    // has no `tagadaToken` field.
    const rawToken = await tokenizer.tokenizeGooglePay(opts.googlePayToken);
    const tagadaToken = btoa(
      JSON.stringify(coreJs.createTagadaDigitalWalletToken(rawToken, 'google_pay')),
    );

    return this.pay({
      checkoutSessionId: opts.checkoutSessionId,
      tagadaToken,
      paymentFlowId: opts.paymentFlowId,
      processorId: opts.processorId,
      paymentMethod: 'google_pay',
      isExpress: true,
    });
  }

  /**
   * Process an APM payment (Klarna, iDEAL, etc.) via redirect.
   * These typically don't require a payment instrument -- the processor handles the redirect.
   */
  async processApm(opts: {
    checkoutSessionId: string;
    paymentMethod: string;
    processorId: string;
    paymentFlowId?: string;
  }): Promise<PayResult> {
    return this.payDirect({
      checkoutSessionId: opts.checkoutSessionId,
      paymentMethod: opts.paymentMethod,
      processorId: opts.processorId,
      paymentFlowId: opts.paymentFlowId,
    });
  }

  /**
   * Initiate a Stripe Express Checkout payment (Apple Pay, Google Pay, Link, etc.).
   *
   * Calls `pay-v2` with `isExpress: true`, which returns a `clientSecret` in
   * `payment.requireActionData.metadata.stripeExpressCheckout.clientSecret`.
   * The caller must then call `stripe.confirmPayment()` with that secret.
   *
   * Used by the `StripeExpressCheckout` class and `useStripeExpress` hook.
   * Not typically called directly — use the Stripe integration classes instead.
   */
  async processStripeExpress(opts: {
    checkoutSessionId: string;
    processorId: string;
    paymentMethod: string;
  }): Promise<PayResult> {
    return this.payDirect({
      checkoutSessionId: opts.checkoutSessionId,
      processorId: opts.processorId,
      paymentMethod: opts.paymentMethod,
      isExpress: true,
    });
  }

  // ---------------------------------------------------------------------------
  // High-level orchestrator — the ONE function beginners should call
  // ---------------------------------------------------------------------------

  /**
   * Process a payment end-to-end: pay, handle 3DS / redirects, poll until done.
   *
   * This is the **recommended** way to process payments. It wraps `pay()`, detects
   * `requireAction`, runs 3DS challenges (if `@tagadapay/core-js` is installed),
   * and polls until a terminal status.
   *
   * Returns a discriminated union so you can pattern-match on `result.status`:
   *
   * @example
   * ```ts
   * const result = await payment.processPayment({
   *   checkoutSessionId: session.id,
   *   tagadaToken,
   * });
   *
   * switch (result.status) {
   *   case 'succeeded':
   *     console.log('Paid!', result.payment.id);
   *     break;
   *   case 'requires_redirect':
   *     // In browser: window.location.href = result.redirectUrl
   *     // The React hook handles this automatically.
   *     break;
   *   case 'failed':
   *     console.error(result.error);
   *     break;
   * }
   * ```
   */
  async processPayment(opts: ProcessPaymentOptions): Promise<ProcessPaymentResult> {
    if (!opts.returnUrl && typeof window !== 'undefined') {
      opts = { ...opts, returnUrl: window.location.href };
    }
    const payResult = await this.pay(opts);
    return this.finalize(payResult, { returnUrl: opts.returnUrl });
  }

  /**
   * Resolve a raw `PayResult` (from any pay endpoint — `pay()`, `payDirect()`,
   * `processApplePay()`, `offers.payPreviewedOffer()`, …) into a typed
   * `ProcessPaymentResult` (`succeeded` / `requires_redirect` / `failed`
   * / `pending`).
   *
   * Handles 3DS routing (modal challenge via core-js, or bank redirect)
   * and async polling until the payment reaches a terminal status.
   *
   * This is the same logic `processPayment()` runs after `pay()` — exposed
   * so callers (and the offers module) that already have a raw `PayResult`
   * don't need to re-pay.
   */
  async finalize(
    input: PayResult,
    opts?: { returnUrl?: string },
  ): Promise<ProcessPaymentResult> {
    const payment = input.payment;
    const returnUrl =
      opts?.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined);

    if (payment.status === 'succeeded') {
      return { status: 'succeeded', payment, order: input.order };
    }

    if (payment.status === 'failed' || payment.status === 'declined') {
      const msg = payment.requireActionData?.message
        || payment.requireActionData?.errorCode
        || `Payment ${payment.status}`;
      return { status: 'failed', error: msg, payment };
    }

    if (payment.requireAction && payment.requireAction !== 'none' && payment.requireActionData) {
      return this.handlePaymentAction(payment, { returnUrl } as ProcessPaymentOptions);
    }

    // 'pending' or 'requires_action' without explicit actionData — poll
    return this.pollUntilDone(payment.id);
  }

  /**
   * Resume a payment after the user returns from a redirect (3DS, processor auth, APM).
   *
   * Called automatically by `usePayment()` in React when it detects return-URL params.
   * You can also call it manually in non-React apps.
   */
  async resumeAfterRedirect(paymentId: string): Promise<ProcessPaymentResult> {
    return this.pollUntilDone(paymentId);
  }

  /**
   * Detect a return from a 3DS / processor redirect by inspecting the
   * current page URL, then poll the payment to completion.
   *
   * The post-3DS URL convention used by Tagada-routed processors is
   * `?paymentAction=requireAction&paymentActionStatus=completed&paymentId=pay_xxx`.
   * If those params are present, this method polls the payment, cleans the
   * URL (so a refresh doesn't re-trigger), and returns the resolved
   * `ProcessPaymentResult`. Otherwise it returns `null`.
   *
   * Use this in vanilla apps as the first thing on page load. The React
   * `usePayment` hook does the same automatically — don't call this when
   * also using `usePayment`, you'll double-poll.
   *
   * @example
   * ```ts
   * const tagada = createHeadlessClient({ storeId, environment });
   *
   * const resumed = await tagada.payment.maybeResumeFromUrl();
   * if (resumed?.status === 'succeeded') {
   *   window.location.href = `/thank-you?orderId=${resumed.order?.id}`;
   *   return;
   * }
   * if (resumed?.status === 'failed') {
   *   showError(resumed.error);
   *   return;
   * }
   * // No 3DS return — render the normal page.
   * ```
   */
  async maybeResumeFromUrl(opts?: {
    /**
     * URL to inspect (defaults to `window.location.href`). Pass an explicit
     * URL when running outside the browser or when integrating with a
     * router that hasn't navigated yet.
     */
    url?: string;
    /**
     * Whether to clean the redirect query params from the address bar
     * once the payment has been resolved. Defaults to `true`.
     */
    cleanUrl?: boolean;
  }): Promise<ProcessPaymentResult | null> {
    if (typeof window === 'undefined' && !opts?.url) return null;

    const href = opts?.url ?? window.location.href;
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return null;
    }

    const action = url.searchParams.get('paymentAction');
    const actionStatus = url.searchParams.get('paymentActionStatus');
    const paymentId = url.searchParams.get('paymentId');
    if (action !== 'requireAction' || actionStatus !== 'completed' || !paymentId) {
      return null;
    }

    const result = await this.resumeAfterRedirect(paymentId);

    // Strip the redirect params so a refresh doesn't re-trigger.
    if (opts?.cleanUrl !== false && typeof window !== 'undefined' && !opts?.url) {
      const cleanParams = new URLSearchParams(window.location.search);
      [
        'paymentAction',
        'paymentActionStatus',
        'paymentId',
        'payment_intent',
        'payment_intent_client_secret',
        'source_type',
        'redirect_status',
        'sessionId',
      ].forEach((k) => cleanParams.delete(k));
      const search = cleanParams.toString();
      try {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + (search ? '?' + search : ''),
        );
      } catch {
        // Best-effort.
      }
    }

    return result;
  }

  // ---------------------------------------------------------------------------
  // Status polling
  // ---------------------------------------------------------------------------

  /** Get current status of a payment. */
  async getPaymentStatus(paymentId: string): Promise<PaymentStatus> {
    return this.http.get<PaymentStatus>(`/api/v1/payments/${paymentId}`);
  }

  /**
   * Poll a payment until it reaches a terminal status.
   * Returns the final status or throws on timeout.
   */
  async pollPaymentStatus(
    paymentId: string,
    opts?: { intervalMs?: number; timeoutMs?: number },
  ): Promise<PaymentStatus> {
    const interval = opts?.intervalMs ?? 1500;
    const timeout = opts?.timeoutMs ?? 60_000;
    const start = Date.now();

    while (Date.now() - start < timeout) {
      const status = await this.getPaymentStatus(paymentId);
      if (status.status === 'succeeded' || status.status === 'failed' || status.status === 'declined') {
        return status;
      }
      await new Promise((r) => setTimeout(r, interval));
    }

    throw new Error(`Payment ${paymentId} did not reach terminal status within ${timeout}ms.`);
  }

  /** Finalize a payment after a 3DS challenge completes. */
  async completeAfter3ds(paymentId: string): Promise<PaymentStatus> {
    return this.http.post<PaymentStatus>('/api/v1/payments/complete-after-3ds', {
      paymentId,
    });
  }

  /** Tell the server this action has been seen / is being processed. */
  async markActionProcessed(paymentId: string): Promise<void> {
    try {
      await this.http.post('/api/v1/payments/require-action/processed', {
        paymentId,
      });
    } catch {
      // Best-effort — don't block the payment flow if this fails
    }
  }

  // ---------------------------------------------------------------------------
  // Internal: action routing
  // ---------------------------------------------------------------------------

  /**
   * Route a payment action to the appropriate handler.
   * Returns `requires_redirect` for browser-level redirects,
   * or attempts 3DS inline and polls to completion.
   */
  private async handlePaymentAction(
    payment: PayResult['payment'],
    opts: ProcessPaymentOptions,
  ): Promise<ProcessPaymentResult> {
    const actionData = payment.requireActionData!;

    await this.markActionProcessed(payment.id);

    const actionType = actionData.type;

    switch (actionType) {
      case 'threeds_auth':
        return this.handleThreedsAuth(payment, opts);

      case 'processor_auth':
      case 'redirect_to_payment':
        return this.extractRedirect(payment, actionData);

      case 'redirect':
        return this.extractRedirect(payment, actionData);

      case 'error':
        return {
          status: 'failed',
          error: actionData.message || actionData.errorCode || 'Payment failed',
          payment,
        };

      default:
        // Unknown action type — try to extract a redirect URL (forward-compatible)
        return this.extractRedirect(payment, actionData);
    }
  }

  /**
   * Extract redirect info from actionData.
   * Works for processor_auth (GET or POST), redirect, and any unknown type with a URL.
   */
  private extractRedirect(
    payment: PayResult['payment'],
    actionData: NonNullable<PayResult['payment']['requireActionData']>,
  ): ProcessPaymentResult {
    const redirect = actionData.metadata?.redirect;
    const url = redirect?.redirectUrl || actionData.redirectUrl;

    if (!url) {
      return {
        status: 'failed',
        error: `Payment requires action (${actionData.type}) but no redirect URL was provided`,
        payment,
      };
    }

    return {
      status: 'requires_redirect',
      redirectUrl: url,
      method: (redirect?.method?.toUpperCase() as 'GET' | 'POST') || 'GET',
      postData: redirect?.data,
      paymentId: payment.id,
    };
  }

  /**
   * Run a 3DS challenge using @tagadapay/core-js.
   * If core-js is not installed, falls back to redirect.
   */
  private async handleThreedsAuth(
    payment: PayResult['payment'],
    opts: ProcessPaymentOptions,
  ): Promise<ProcessPaymentResult> {
    const session = payment.requireActionData?.metadata?.threedsSession;

    // If no 3DS session data, fall back to redirect (acsChallengeUrl)
    if (!session?.externalSessionId) {
      return this.extractRedirect(payment, payment.requireActionData!);
    }

    // Try to run the challenge inline via core-js
    let coreJs: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      coreJs = await import('@tagadapay/core-js');
    } catch {
      // core-js not installed — fall back to redirect if possible
      if (session.acsChallengeUrl) {
        return {
          status: 'requires_redirect',
          redirectUrl: session.acsChallengeUrl,
          paymentId: payment.id,
        };
      }
      return this.extractRedirect(payment, payment.requireActionData!);
    }

    // Run BasisTheory 3DS challenge in a modal iframe
    try {
      const manager = new coreJs.ThreedsManager({ provider: 'basis_theory' });
      await manager.initialize({ apiKey: undefined });

      await manager.startChallenge({
        sessionId: session.externalSessionId,
        acsChallengeUrl: session.acsChallengeUrl,
        acsTransactionId: session.acsTransID,
        threeDSVersion: session.messageVersion,
      });

      // Challenge done — tell backend and poll
      await this.completeAfter3ds(payment.id);
      return this.pollUntilDone(payment.id);
    } catch {
      // 3DS challenge failed — fall back to redirect
      if (session.acsChallengeUrl) {
        return {
          status: 'requires_redirect',
          redirectUrl: session.acsChallengeUrl,
          paymentId: payment.id,
        };
      }
      return {
        status: 'failed',
        error: '3D Secure authentication failed',
        payment,
      };
    }
  }

  /**
   * Poll until the payment reaches a terminal status, then return a ProcessPaymentResult.
   * Also handles new requireAction that may appear during polling.
   */
  private async pollUntilDone(
    paymentId: string,
    opts?: { intervalMs?: number; timeoutMs?: number },
  ): Promise<ProcessPaymentResult> {
    const interval = opts?.intervalMs ?? 1500;
    const timeout = opts?.timeoutMs ?? 60_000;
    const start = Date.now();
    let consecutiveErrors = 0;

    while (Date.now() - start < timeout) {
      let status: PaymentStatus;
      try {
        status = await this.getPaymentStatus(paymentId);
        consecutiveErrors = 0;
      } catch {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          return { status: 'failed', error: 'Payment verification failed due to network errors' };
        }
        await new Promise((r) => setTimeout(r, interval));
        continue;
      }

      if (
        status.status === 'succeeded' ||
        (status.status === 'pending' && status.subStatus === 'authorized')
      ) {
        return { status: 'succeeded', payment: status as unknown as PayResult['payment'], order: status.order };
      }

      if (status.status === 'failed' || status.status === 'declined') {
        return { status: 'failed', error: status.status, payment: status as unknown as PayResult['payment'] };
      }

      // New unprocessed action appeared during polling
      if (
        status.requireAction &&
        status.requireAction !== 'none' &&
        status.requireActionData &&
        !status.requireActionData.processed
      ) {
        return this.handlePaymentAction(
          status as unknown as PayResult['payment'],
          {} as ProcessPaymentOptions,
        );
      }

      await new Promise((r) => setTimeout(r, interval));
    }

    return { status: 'pending', paymentId };
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Read paymentSetupConfig from the Tagada routing injection.
   * Only available when running on Tagada infra (not on external hosting like Vercel).
   */
  private readInjectedStepConfig(): PaymentSetupConfig | null {
    if (typeof window === 'undefined') return null;
    const raw = (window as unknown as Record<string, unknown>).__TGD_STEP_CONFIG__;
    if (!raw) return null;
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return (parsed as Record<string, unknown>)?.paymentSetupConfig as PaymentSetupConfig ?? null;
    } catch {
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async loadCoreJs(): Promise<any> {
    try {
      return await import('@tagadapay/core-js');
    } catch {
      throw new Error(
        'This method requires @tagadapay/core-js. Install it: npm install @tagadapay/core-js',
      );
    }
  }
}
